/**
 * VeterinariosView.js - Vista de administración de Veterinarios
 */
import React, { useState, useCallback, useEffect } from 'react';
import { obtenerVeterinariosAdmin } from '../../../services/api/modules/admin';

const VeterinariosView = ({ addToHistory }) => {
  const [veterinarios, setVeterinarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedVet, setSelectedVet] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const pageSize = 20;

  const loadVeterinarios = useCallback(async (page = 1, resetFilters = false) => {
    setLoading(true);
    try {
      const filtros = resetFilters ? {} : {
        search: searchQuery,
        estado: estadoFilter
      };

      const result = await obtenerVeterinariosAdmin(filtros, pageSize, page);

      if (result.error) {
        console.error('Error:', result.error);
        setVeterinarios([]);
      } else {
        setVeterinarios(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalRecords(result.totalRecords || 0);
        setCurrentPage(result.currentPage || page);
      }
      setHasSearched(true);
    } catch (error) {
      console.error('Error cargando veterinarios:', error);
      setVeterinarios([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, estadoFilter]);

  useEffect(() => {
    loadVeterinarios(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadVeterinarios(1);
    addToHistory(`Buscar veterinarios: "${searchQuery || 'todos'}"`);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setEstadoFilter('');
    setCurrentPage(1);
    loadVeterinarios(1, true);
    addToHistory('Filtros de veterinarios limpiados');
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    loadVeterinarios(newPage);
    addToHistory(`Veterinarios - Página ${newPage}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      verificado: 'bg-success',
      pendiente: 'bg-warning text-dark',
      noAceptado: 'bg-danger',
      inactivo: 'bg-secondary'
    };
    return badges[estado] || 'bg-secondary';
  };

  return (
    <div className="veterinarios-view">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="bi bi-heart-pulse me-2"></i>
          Veterinarios
        </h5>
        <span className="badge bg-primary">{totalRecords} registros</span>
      </div>

      {/* Formulario de busqueda */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="row g-2">
          <div className="col-12 col-md-5">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, email o clinica..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="col-6 col-md-3">
            <select
              className="form-select"
              value={estadoFilter}
              onChange={(e) => {
                setEstadoFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos los estados</option>
              <option value="verificado">Verificado</option>
              <option value="pendiente">Pendiente</option>
              <option value="noAceptado">No Aceptado</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>

          <div className="col-6 col-md-2">
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {loading ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <>
                  <i className="bi bi-search me-1"></i>
                  Buscar
                </>
              )}
            </button>
          </div>

          <div className="col-12 col-md-2">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={handleClearFilters}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Limpiar
            </button>
          </div>
        </div>
      </form>

      {/* Resultados */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando veterinarios...</p>
        </div>
      ) : veterinarios.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th style={{width: '200px'}}>ID Firestore</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Telefono</th>
                  <th>Clinica</th>
                  <th>Estado</th>
                  <th>Registro</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {veterinarios.map((vet) => (
                  <tr key={vet.id}>
                    <td>
                      <code className="small text-break" style={{fontSize: '0.75rem'}}>
                        {vet.id}
                      </code>
                    </td>
                    <td>{vet.nombre || 'Sin nombre'}</td>
                    <td>
                      <small>{vet.correo || 'N/A'}</small>
                    </td>
                    <td>{vet.telefono || 'N/A'}</td>
                    <td>{vet.clinica || 'N/A'}</td>
                    <td>
                      <span className={`badge ${getEstadoBadge(vet.estado)}`}>
                        {vet.estado || 'desconocido'}
                      </span>
                    </td>
                    <td>
                      <small>{formatDate(vet.createdAt)}</small>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => setSelectedVet(vet)}
                        title="Ver detalles"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginacion */}
          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    <i className="bi bi-chevron-left"></i> Anterior
                  </button>
                </li>

                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }

                  if (pageNum < 1 || pageNum > totalPages) return null;

                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                  >
                    Siguiente <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
              <div className="text-center mt-2">
                <small className="text-muted">
                  Pagina {currentPage} de {totalPages} - {totalRecords} resultados
                </small>
              </div>
            </nav>
          )}
        </>
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3">No se encontraron veterinarios</p>
          {(estadoFilter || searchQuery) && (
            <button className="btn btn-outline-primary" onClick={handleClearFilters}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Limpiar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-heart-pulse" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3">Usa la busqueda para encontrar veterinarios</p>
        </div>
      )}

      {/* Modal de detalles */}
      {selectedVet && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-badge me-2"></i>
                  Detalles del Veterinario
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedVet(null)} />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-12 mb-3">
                    <div className="alert alert-secondary">
                      <strong>ID Firestore:</strong>
                      <br />
                      <code className="user-select-all">{selectedVet.id}</code>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <p><strong>Nombre:</strong> {selectedVet.nombre || 'N/A'}</p>
                    <p><strong>Email:</strong> {selectedVet.correo || 'N/A'}</p>
                    <p><strong>Telefono:</strong> {selectedVet.telefono || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Clinica:</strong> {selectedVet.clinica || 'N/A'}</p>
                    <p>
                      <strong>Estado:</strong>{' '}
                      <span className={`badge ${getEstadoBadge(selectedVet.estado)}`}>
                        {selectedVet.estado || 'desconocido'}
                      </span>
                    </p>
                    <p><strong>Fecha Registro:</strong> {formatDate(selectedVet.createdAt)}</p>
                  </div>

                  {selectedVet.invitadoPor && selectedVet.invitadoPor.length > 0 && (
                    <div className="col-12 mt-3">
                      <strong>Invitado por:</strong>
                      <ul className="list-group mt-2">
                        {selectedVet.invitadoPor.map((inv, idx) => (
                          <li key={idx} className="list-group-item">
                            <small>
                              <strong>Owner ID:</strong> <code>{inv.ownerId}</code><br />
                              <strong>Pet ID:</strong> <code>{inv.objetoId}</code><br />
                              <strong>Fecha:</strong> {formatDate(inv.fechaInvitacion)}
                            </small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedVet(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VeterinariosView;
